"use client";
import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import {
  chakra,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { RankingInfo } from "@tanstack/match-sorter-utils";
import { flexRender, Header } from "@tanstack/react-table";
import { DetailsTableFilterInput } from "./filter-input";
import {
  useDetailsTable,
  UseDetailsTableOptions,
} from "./hooks/use-details-table";

declare module "@tanstack/table-core" {
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

export interface DetailsTableProps<DataProps extends any>
  extends UseDetailsTableOptions<DataProps> {
  nonSortableHeaders?: string[];
  category?: string;
}

export function DetailsTable<DataProps extends any>({
  data,
  columns,
  nonSortableHeaders,
  category,
}: DetailsTableProps<DataProps>) {
  const { table, globalFilter, setGlobalFilter } = useDetailsTable<DataProps>({
    data,
    columns,
  });

  return (
    <TableContainer apply="styles.wrapperStyles.scrollable">
      {category !== "Hurricanes" ? (
        <DetailsTableFilterInput
          value={globalFilter ?? ""}
          onChange={(value) => setGlobalFilter(String(value))}
          placeholder="Search..."
          sx={{
            border: "2px solid #F8FAFC",
            borderRadius: 3,
            height: 8,
            fontSize: 14,
            left: 0,
            margin: "10px 0px 25px 5px",
            padding: "15px",
            position: "sticky",
            top: 0,
          }}
        />
      ) : (
        <></>
      )}

      <Table size="sm">
        <Thead bg="#F4F6F8">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header: Header<DataProps, unknown>) => {
                return (
                  <Th
                    key={header.id}
                    onClick={
                      !nonSortableHeaders?.includes(header.id)
                        ? header.column.getToggleSortingHandler()
                        : undefined
                    }
                    textAlign="center"
                    cursor={
                      !nonSortableHeaders?.includes(header.id)
                        ? "pointer"
                        : "auto"
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    <chakra.span pl="2">
                      {header.column.getIsSorted() ? (
                        header.column.getIsSorted() === "desc" ? (
                          <TriangleDownIcon aria-label="sorted descending" />
                        ) : (
                          <TriangleUpIcon aria-label="sorted ascending" />
                        )
                      ) : null}
                    </chakra.span>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody bg="white">
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id} minW="200px" textAlign="center">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
}
